import {
  Button,
  Dropdown,
  DropdownItem,
  FormControl,
  IconButton,
  openDialog,
  showNotification,
} from 'platform/components';
import {Box, HStack, Show, Text} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import i18n from '@dms/i18n';
import {useTenant} from '@dms/shared';

import {pushGtmEvent} from 'shared/web';

import {useDocumentExtractionBehaviour} from '../hooks/useDocumentExtractionBehaviour';
import {useRecalculateItemPrice} from '../hooks/useRecalculateItemPrice';
import {DeliveryNoteDocumentPreviewFormDefinition} from '../types/DeliveryNoteDocumentPreviewFormDefinition';
import {ItemPriceSource} from '../types/ItemPriceSource';
import {ChangeManufacturer} from './ChangeManufacturer';
import {ChangeUnit} from './ChangeUnit';
import {ChangeVat} from './ChangeVat';

interface BulkActionsBannerProps {
  isConfirmed: boolean;
  currentPage: number;
  formApi: UseFormReturn<DeliveryNoteDocumentPreviewFormDefinition>;
  control: FormControl<DeliveryNoteDocumentPreviewFormDefinition>;
}

export function BulkActionsBanner(props: BulkActionsBannerProps) {
  const behaviour = useDocumentExtractionBehaviour();
  const {tenantCountry} = useTenant();
  const recalculateItemPrice = useRecalculateItemPrice(props.formApi, tenantCountry);
  const listOfItems = useWatch({control: props.control, name: 'listOfItems'});

  const hasSelectedItems = listOfItems.some((item) => item.isSelected);

  if (!props.isConfirmed || !hasSelectedItems) {
    return null;
  }

  const currentPageItems = listOfItems.filter((item) => item.pageNumber === props.currentPage);
  const selectedOnCurrentPageCount = currentPageItems.filter((item) => item.isSelected).length;
  const allOnCurrentPageSelected =
    currentPageItems.length > 0 && selectedOnCurrentPageCount === currentPageItems.length;

  const totalItemsCount = listOfItems.length;
  const allGloballySelected = listOfItems.every((item) => item.isSelected);

  const pageNumbers = new Set(listOfItems.map((item) => item.pageNumber));
  const hasMoreThanOnePage = pageNumbers.size > 1;

  const shouldShowSelectAllLink =
    allOnCurrentPageSelected && hasMoreThanOnePage && !allGloballySelected;

  const handleSelectAllGlobally = () => {
    pushGtmEvent({event: 'button_click', event_id: 'ai_tool_dn_select_all_items_globally'});
    listOfItems.forEach((_, index) => {
      props.formApi.setValue(`listOfItems.${index}.isSelected`, true);
    });
  };

  const clearSelections = () => {
    listOfItems.forEach((_, index) => {
      props.formApi.setValue(`listOfItems.${index}.isSelected`, false);
    });
  };

  const handleClearAllSelections = () => {
    pushGtmEvent({event: 'button_click', event_id: 'ai_tool_dn_clear_all_selections'});
    clearSelections();
  };

  const applyToSelected = (apply: (index: number) => void) => {
    listOfItems.forEach((item, index) => {
      if (!item.isSelected) {
        return;
      }
      apply(index);
    });
  };

  const handleOpenChangeVat = () => {
    pushGtmEvent({event: 'button_click', event_id: 'ai_tool_dn_open_change_vat_dialog'});
    openDialog(
      <ChangeVat
        dialogId={CHANGE_VAT_DIALOG_ID}
        onAssign={(vatType) =>
          applyToSelected((index) => props.formApi.setValue(`listOfItems.${index}.vat`, vatType))
        }
      />,
      {
        id: CHANGE_VAT_DIALOG_ID,
        title: i18n.t('entity.documentExtraction.actions.changeVat'),
        size: 'small',
      }
    );
  };

  const handleOpenChangeUnit = () => {
    pushGtmEvent({event: 'button_click', event_id: 'ai_tool_dn_open_change_unit_dialog'});
    openDialog(
      <ChangeUnit
        dialogId={CHANGE_UNIT_DIALOG_ID}
        onAssign={(unitId) =>
          applyToSelected((index) => props.formApi.setValue(`listOfItems.${index}.unit`, unitId))
        }
      />,
      {
        id: CHANGE_UNIT_DIALOG_ID,
        title: i18n.t('entity.documentExtraction.actions.changeUnit'),
        size: 'small',
      }
    );
  };

  const handleOpenChangeManufacturer = () => {
    pushGtmEvent({
      event: 'button_click',
      event_id: 'ai_tool_dn_open_bulk_assign_manufacturer_dialog',
    });
    openDialog(
      <ChangeManufacturer
        dialogId={CHANGE_MANUFACTURER_DIALOG_ID}
        onAssign={(manufacturerId) =>
          applyToSelected((index) =>
            props.formApi.setValue(`listOfItems.${index}.manufacturer`, manufacturerId)
          )
        }
      />,
      {
        id: CHANGE_MANUFACTURER_DIALOG_ID,
        title: i18n.t('entity.documentExtraction.actions.changeManufacturer'),
        size: 'small',
      }
    );
  };

  const handleRecalculatePrices = (source: ItemPriceSource) => {
    pushGtmEvent({event: 'button_click', event_id: 'ai_tool_dn_price_calculation'});
    let hasInvalidVat = false;
    applyToSelected((index) => {
      const item = props.formApi.getValues(`listOfItems.${index}`);
      const isVatValid = recalculateItemPrice({
        index,
        source,
        quantity: item.quantity,
        price: source === 'unitPrice' ? item.unitPrice : item.totalPrice,
      });
      if (!isVatValid) {
        hasInvalidVat = true;
      }
    });
    if (hasInvalidVat) {
      showNotification.warning(
        i18n.t('entity.documentExtraction.notifications.pricesNotCalculatedProperly')
      );
    }
    clearSelections();
  };

  const handleIncludeInNote = () => {
    pushGtmEvent({event: 'button_click', event_id: 'ai_tool_dn_include_in_note'});
    applyToSelected((index) => props.formApi.setValue(`listOfItems.${index}.isActive`, true));
  };

  const handleExcludeFromNote = () => {
    pushGtmEvent({event: 'button_click', event_id: 'ai_tool_dn_exclude_from_note'});
    applyToSelected((index) => props.formApi.setValue(`listOfItems.${index}.isActive`, false));
  };

  return (
    <Box
      paddingHorizontal={2}
      paddingVertical={3}
      backgroundColor="palettes.neutral.10.100"
      borderTopLeftRadius="small"
      borderTopRightRadius="small"
      borderBottom="1px solid"
      borderColor="palettes.neutral.40.100"
    >
      <HStack justify="space-between" align="center" spacing={4}>
        <HStack align="center" spacing={2}>
          <Text size="small">
            {allGloballySelected
              ? i18n.t('entity.documentExtraction.labels.allItemsSelected', {
                  count: totalItemsCount,
                })
              : i18n.t('entity.documentExtraction.labels.itemsOnPageSelected', {
                  count: selectedOnCurrentPageCount,
                })}
          </Text>
          <Show when={shouldShowSelectAllLink}>
            <Button
              size="small"
              variant="link"
              title={i18n.t('entity.documentExtraction.actions.selectAllItems', {
                count: totalItemsCount,
              })}
              onClick={handleSelectAllGlobally}
            />
          </Show>
        </HStack>
        <HStack align="center" spacing={1}>
          <Button
            size="small"
            variant="ghost"
            title={i18n.t('entity.documentExtraction.actions.changeVat')}
            onClick={handleOpenChangeVat}
          />
          <Button
            size="small"
            variant="ghost"
            title={i18n.t('entity.documentExtraction.actions.changeUnit')}
            onClick={handleOpenChangeUnit}
          />
          <Button
            size="small"
            variant="ghost"
            title={i18n.t('entity.documentExtraction.actions.changeManufacturer')}
            onClick={handleOpenChangeManufacturer}
          />
          <Show when={behaviour.isPriceRecalculationEnabled}>
            <Box height={6} width="calc(1px)" backgroundColor="general.separator" />
            <Dropdown
              dropdownControl={
                <Button
                  size="small"
                  variant="ghost"
                  title={i18n.t('entity.documentExtraction.actions.recalculatePrice')}
                  aria-label={i18n.t('entity.documentExtraction.actions.recalculatePrice')}
                  rightIcon="navigation/expand_more"
                />
              }
            >
              <DropdownItem
                label={i18n.t('entity.documentExtraction.actions.unitPriceFromTotalPrice')}
                onClick={() => handleRecalculatePrices('totalPrice')}
              />
              <DropdownItem
                label={i18n.t('entity.documentExtraction.actions.totalPriceFromUnitPrice')}
                onClick={() => handleRecalculatePrices('unitPrice')}
              />
            </Dropdown>
          </Show>
          <Box height={6} width="calc(1px)" backgroundColor="general.separator" />
          <Button
            size="small"
            variant="ghost"
            title={i18n.t('entity.documentExtraction.actions.includeInNote')}
            isDisabled={behaviour.areSourceItemsReadOnly}
            onClick={handleIncludeInNote}
          />
          <Button
            size="small"
            variant="ghost"
            title={i18n.t('entity.documentExtraction.actions.excludeFromNote')}
            isDisabled={behaviour.areSourceItemsReadOnly}
            onClick={handleExcludeFromNote}
          />
          <Box height={6} width="calc(1px)" backgroundColor="general.separator" />
          <IconButton icon="navigation/cancel" size="small" onClick={handleClearAllSelections} />
        </HStack>
      </HStack>
    </Box>
  );
}

const CHANGE_VAT_DIALOG_ID = 'change-vat-dialog';
const CHANGE_UNIT_DIALOG_ID = 'change-unit-dialog';
const CHANGE_MANUFACTURER_DIALOG_ID = 'change-manufacturer-dialog';
